import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import Loading from "src/components/Loading";
import { useOffer } from "src/hooks/offer";
import { getSection } from "src/hooks/useContentful/getSection";
import { useFetchContent } from "src/hooks/useContentful/useFetchContent";
import CardPlan from "./CardPlan";
import contentfulClient from "../../../../services/contentful";
import {
  Button,
  Container,
  ContainerPlan,
  ErrorText,
  LoadingCupom,
  Modal,
  ModalDescription,
  ModalInput,
  ModalLink,
  ModalLinkContainer,
  ModalTitle,
  Plans,
  Sorriso,
  Title,
  IconButton,
  ModalLinkContent,
} from "./styles";
import Icon from "src/components/Icons";
import Tooltip from "@material-ui/core/Tooltip";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import Footer from "./Footer";
import { useMiddlewareOffer } from "src/hooks/useMiddlewareOffer";
import { useValidateTagOffer } from "src/hooks/useValidateTag/useValidateTagOffer";
import { useMedia } from "use-media";

function Planos() {
  const content = useFetchContent();
  const [modalOpen, setModalOpen] = useState(true);
  const [openTooltip, setOpenTooltip] = useState(false);
  const [tagNumber, setTagNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { setCurrentOffer } = useOffer();
  const { data, isLoading, isError } = useMiddlewareOffer();
  const {
    data: tagData,
    isLoading: tagLoading,
    isError: tagError,
    refetch,
  } = useValidateTagOffer(tagNumber);
  const [width, setWidth] = useState(window.innerWidth);
  const mediaPhone = 767;
  const isMobile = useMedia({ maxWidth: 767 });

  const theme = createMuiTheme({
    overrides: {
      MuiTooltip: {
        tooltip: {
          fontFamily: "Open Sans",
          fontSize: "0.75rem",
          padding: "8px",
          color: "#230c87",
          backgroundColor: "#fff",
          borderRadius: "10px",
          maxWidth: width <= mediaPhone ? "300px" : "310px",
        },
      },
    },
  });

  const promotionalCodes = useMemo(
    () => content.data.fields.promotionalCodes.split("@")[0],
    [content.data.fields.promotionalCodes]
  );

  const hasCoupon = useMemo(() => content.data.fields.hasCoupon, [
    content.data.fields.hasCoupon,
  ]);

  const section = getSection({
    sectionName: "landingDefaultPromo",
    data: content.data,
  });
  const sectionCupom = getSection({
    sectionName: "landingCouponCard",
    data: content.data,
  });

  const typePlans = ["pedagiosEstacionamentos", "estacionamentos", "pedagios"];

  const plans = useQuery(
    "getCardPlan",
    () =>
      Promise.all(
        typePlans
          .filter((field) => Object.keys(section).includes(field))
          .map(
            async (field) =>
              await contentfulClient.getEntry(section[field].sys.id)
          )
      ),
    {
      enabled: content.data && !content.isLoading,
      retry: false,
    }
  );

  // eslint-disable-next-line no-unused-vars
  const tagValidated = useMemo(() => {
    if (!tagData) return null;
    if (tagData.valido === true) {
      setCurrentOffer(promotionalCodes);
      return true;
    } else if (tagData.valido === false) {
      setError("Número da tag inválida.");
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagData]);

  const handleLoadOffer = async () => {
    refetch();
  };

  const handleCloseTooltip = () => {
    setOpenTooltip(false);
  };

  const handleOpenTooltip = () => {
    setOpenTooltip(true);
  };

  useEffect(() => {
    setLoading(plans.isLoading || content.isLoading);
  }, [plans.isLoading, content.isLoading]);

  useEffect(() => {
    setModalOpen(hasCoupon && (!data || isLoading));
  }, [hasCoupon, data, isLoading]);

  useEffect(() => {
    if (isError || tagError)
      setError(
        "Estamos com problemas para carregar os planos, tente novamente mais tarde."
      );
  }, [isError, tagError]);

  useEffect(() => {
    const handleResizeWindow = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResizeWindow);
    return () => {
      window.removeEventListener("resize", handleResizeWindow);
    };
  }, [setWidth]);

  if (loading || !plans.data) return <Loading />;

  return (
    <Container id="plans">
      <Title>{section.title}</Title>
      <Sorriso />
      <ContainerPlan>
        <Plans size={plans.data.length}>
          {plans.data?.map((plan, index) => (
            <CardPlan item={plan} data={data} key={index} blur={modalOpen} />
          ))}
        </Plans>
        {!isMobile && (
          <Footer
            buttonColor={section.buttonColor}
            terms={section.regulamento}
            textColor={"#FFFFFF"}
            modalOpen={modalOpen}
            cupons={[
              plans.data[0].fields.firstButtonCoupon,
              plans.data[0].fields.secondButtonCoupon,
            ]}
            disabled={modalOpen}
          />
        )}
      </ContainerPlan>
      {isMobile && (
        <Footer
          buttonColor={section.buttonColor}
          terms={section.regulamento}
          textColor={"#FFFFFF"}
          modalOpen={modalOpen}
          cupons={[
            plans.data[0].fields.firstButtonCoupon,
            plans.data[0].fields.secondButtonCoupon,
          ]}
          disabled={modalOpen}
        />
      )}
      {modalOpen && (
        <Modal
          open={modalOpen}
          backgroundColor={sectionCupom.couponCardBgColor}
          isError={error}
        >
          <ModalTitle color={sectionCupom.couponCardTitleColor}>
            {sectionCupom.couponCardTitle}
          </ModalTitle>
          <ModalDescription>{sectionCupom.couponCardAuxText}</ModalDescription>
          <ModalInput
            placeholder="Número da tag"
            value={tagNumber}
            maxLength={10}
            onChange={(e) => {
              if (error) setError(null);
              setTagNumber(e.target.value.toUpperCase());
            }}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
          />
          <ModalLinkContainer isError={error}>
            <MuiThemeProvider theme={theme}>
              <ModalLinkContent
                onMouseEnter={handleOpenTooltip}
                onMouseLeave={handleCloseTooltip}
              >
                <ModalLink>Onde encontrar o número</ModalLink>
                <Tooltip
                  title={
                    "A numeração encontra-se do lado esquerdo da tag, embaixo do QR Code. São 10 números."
                  }
                  placement={"bottom"}
                  enterTouchDelay={isMobile && 0}
                  open={openTooltip}
                >
                  <IconButton>
                    <Icon
                      name="Atention"
                      style={{
                        filter: "brightness(150)",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </ModalLinkContent>
            </MuiThemeProvider>
          </ModalLinkContainer>
          {error && <ErrorText>{error}</ErrorText>}
          <Button
            className="botaoConfirmar"
            disabled={
              loading ||
              plans.isLoading ||
              !tagNumber ||
              tagLoading ||
              isLoading
            }
            color={sectionCupom.couponCardPrimaryButtonTextColor}
            backgroundColor={sectionCupom.couponCardPrimaryButtonColor}
            onClick={() => {
              handleLoadOffer();
            }}
          >
            {isLoading ? (
              <LoadingCupom
                src="/gwmbrasil/assets/loading.svg"
                alt="Carregando"
              />
            ) : (
              `${sectionCupom.textoDoBotaoPrimario}`
            )}
          </Button>
        </Modal>
      )}
    </Container>
  );
}
export default Planos;
